/**
 * Shared geometry for text overlays.
 *
 * The same text is drawn in four places — the inline player, the fullscreen
 * player, the position picker's preview, and the exported MP4. They only look
 * alike if every dimension is a *fraction of the 4:3 video frame* rather than a
 * fixed pixel size. Hardcoded `text-4xl`/`p-8` looked correct at one player
 * width and wrong at every other one: the fullscreen player is several times
 * wider than the inline player, so identical pixels read as half the size.
 *
 * The ratios below reproduce the inline player at its design width
 * (`max-w-2xl` = 672px) with the classes it used to hardcode: `text-4xl` inside
 * an `md:p-8` container, `px-6 py-3 rounded-lg` pills, `gap-2` between stacked
 * items. That is the reference look; everything else now scales to match it.
 *
 * On screen the ratios become `cqi` units against the frame element (see
 * `overlayViewportStyle` / `overlayFrameStyle`). On canvas they become pixels
 * via `overlayPixels()`. There is no pixel size anywhere in the on-screen path,
 * so the text scales without bound — a 4K fullscreen frame and a 375px phone
 * resolve the same fraction.
 *
 * ⚠️ **Do not wrap these in `clamp()`.** Bounding fluid type is the correct
 * default for UI text (moderncss.dev), but this text is *picture content*: it
 * gets burned into a fixed 960x720 MP4 by `exportTextOverlay.ts`, which cannot
 * clamp. A min/max on the player would make it disagree with the export again
 * at exactly the extremes — the bug this file exists to prevent.
 *
 * Sizing text as a fraction of the video is the standard approach: WebVTT's
 * default cue style is `5vh sans-serif` (5% of video height), and Netflix's
 * timed-text guide sizes subtitles to fit 42 characters across the frame.
 * Our 5.357% of width is 7.14% of height — larger than a subtitle, which is
 * right for a title card, and roughly 12% above Netflix's implied subtitle size.
 */

import type { CSSProperties } from 'react';

export type TextPosition =
  | 'top-left' | 'top-center' | 'top-right'
  | 'middle-left' | 'middle-center' | 'middle-right'
  | 'bottom-left' | 'bottom-center' | 'bottom-right';

export const TEXT_POSITIONS: TextPosition[] = [
  'top-left', 'top-center', 'top-right',
  'middle-left', 'middle-center', 'middle-right',
  'bottom-left', 'bottom-center', 'bottom-right',
];

/** Every surface renders text over a 4:3 frame (matches the export canvas). */
export const OVERLAY_ASPECT_W = 4;
export const OVERLAY_ASPECT_H = 3;

/** Frame width the ratios were derived from (Tailwind `max-w-2xl`). */
const REFERENCE_WIDTH = 672;

/** Fractions of the frame width. */
export const OVERLAY_RATIOS = {
  fontSize: 36 / REFERENCE_WIDTH,      // text-4xl
  containerPad: 32 / REFERENCE_WIDTH,  // md:p-8
  pillPadX: 24 / REFERENCE_WIDTH,      // px-6
  pillPadY: 12 / REFERENCE_WIDTH,      // py-3
  pillRadius: 8 / REFERENCE_WIDTH,     // rounded-lg
  itemGap: 8 / REFERENCE_WIDTH,        // gap-2
} as const;

/** Unitless leading, from Tailwind's text-4xl (40px line on a 36px font). */
export const OVERLAY_LINE_HEIGHT = 40 / 36;

/** Weight of overlay text. Normal — the pill background carries the contrast. */
export const OVERLAY_FONT_WEIGHT = 400;

// `cqi` (container inline size) rather than `cqw` — the recommended unit for
// fluid type. Identical to cqw in horizontal writing modes, correct in others.
const cqi = (ratio: number) => `${(ratio * 100).toFixed(4)}cqi`;

/**
 * Put on the element that fills the player/preview box. Establishes a size
 * container so the frame below can letterbox itself with `cqw`/`cqh`.
 * (Letterboxing needs both axes, so this one is `size`, not `inline-size`.)
 */
export const overlayViewportStyle: CSSProperties = {
  containerType: 'size',
} as CSSProperties;

/**
 * The 4:3 frame itself, letterboxed inside the viewport exactly the way the
 * `<video>`'s `object-contain` letterboxes the picture — so text stays on the
 * picture in fullscreen instead of drifting into the black bars. It is also the
 * `cqi` basis for every measurement below.
 */
export const overlayFrameStyle: CSSProperties = {
  width: `min(100cqw, calc(100cqh * ${OVERLAY_ASPECT_W} / ${OVERLAY_ASPECT_H}))`,
  aspectRatio: `${OVERLAY_ASPECT_W} / ${OVERLAY_ASPECT_H}`,
  containerType: 'inline-size',
} as CSSProperties;

/** Padding/gap for a position container inside the frame. */
export const overlayContainerStyle: CSSProperties = {
  padding: cqi(OVERLAY_RATIOS.containerPad),
  gap: cqi(OVERLAY_RATIOS.itemGap),
};

/** Type and padding for a single text pill inside the frame. */
export const overlayPillStyle: CSSProperties = {
  fontSize: cqi(OVERLAY_RATIOS.fontSize),
  lineHeight: OVERLAY_LINE_HEIGHT,
  fontWeight: OVERLAY_FONT_WEIGHT,
  padding: `${cqi(OVERLAY_RATIOS.pillPadY)} ${cqi(OVERLAY_RATIOS.pillPadX)}`,
  borderRadius: cqi(OVERLAY_RATIOS.pillRadius),
};

/** The same ratios in pixels, for canvas rendering (export). */
export function overlayPixels(frameWidth: number) {
  const fontSize = OVERLAY_RATIOS.fontSize * frameWidth;
  return {
    fontSize,
    lineHeight: fontSize * OVERLAY_LINE_HEIGHT,
    containerPad: OVERLAY_RATIOS.containerPad * frameWidth,
    pillPadX: OVERLAY_RATIOS.pillPadX * frameWidth,
    pillPadY: OVERLAY_RATIOS.pillPadY * frameWidth,
    pillRadius: OVERLAY_RATIOS.pillRadius * frameWidth,
    itemGap: OVERLAY_RATIOS.itemGap * frameWidth,
  };
}

/** Flexbox anchoring for each of the 9 positions, within the frame. */
export const OVERLAY_POSITION_CLASSES: Record<TextPosition, string> = {
  'top-left': 'top-0 left-0 right-0 items-start justify-start',
  'top-center': 'top-0 left-0 right-0 items-center justify-start',
  'top-right': 'top-0 left-0 right-0 items-end justify-start',
  'middle-left': 'top-1/2 -translate-y-1/2 left-0 right-0 items-start justify-center',
  'middle-center': 'top-1/2 -translate-y-1/2 left-0 right-0 items-center justify-center',
  'middle-right': 'top-1/2 -translate-y-1/2 left-0 right-0 items-end justify-center',
  'bottom-left': 'bottom-0 left-0 right-0 items-start justify-end',
  'bottom-center': 'bottom-0 left-0 right-0 items-center justify-end',
  'bottom-right': 'bottom-0 left-0 right-0 items-end justify-end',
};

/** Text alignment implied by a position's horizontal half. */
export const getTextAlignment = (position: TextPosition): string => {
  if (position.includes('left')) return 'text-left';
  if (position.includes('right')) return 'text-right';
  return 'text-center';
};

/** Classes shared by every pill, on screen. Geometry comes from the styles. */
export const OVERLAY_PILL_CLASSES =
  'text-white bg-black/40 backdrop-blur-sm border border-white/20 whitespace-pre-wrap max-w-full';
